import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { extractPerson, useAuthor } from "@/lib/authors";
import { PillButton } from "@/components/RainbowButton";
import { ArrowLeft, Loader2, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import UserAvatar from "@/components/UserAvatar";

/**
 * List page for a user's followers or following.
 *
 * Port of iOS `mobile/src/app/(app)/u/[username]/followers.tsx` and
 * `following.tsx` (see spec 15_OTHER_USER.md §3–4). The response from
 * `by_username_followers` / `by_username_following` can be:
 *   • A flat list of people.
 *   • Follow-edge rows with the person nested under
 *     `follower` / `_follower` / `user` / `_user` / `profile` / `_profile` / `author`.
 *   • Bare id-only edges — PersonRow then hydrates identity + avatar
 *     via `useAuthor(user_id)` → `GET /users/{id}`.
 *
 * `extractPerson(it, kind)` flattens the first two cases; the third
 * yields `{ user_id }`, and PersonRow does the fetch.
 *
 * @param {"followers"|"following"} mode
 */
export default function UserListPage({ mode }) {
  const { username } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [followingIds, setFollowingIds] = useState(new Set());

  const endpoint =
    mode === "followers"
      ? EP.profileFollowersByUsername
      : EP.profileFollowingByUsername;
  const kind = mode === "followers" ? "follower" : "following";

  const load = useCallback(
    async (p) => {
      setLoading(true);
      setErr("");
      try {
        const res = await api.get(endpoint(username, p, 20));
        const d = unwrap(res.data);
        const list = Array.isArray(d)
          ? d
          : d?.items ||
            d?.users ||
            d?.followers ||
            d?.following ||
            d?.data ||
            [];
        // Follow-edge rows can nest the person under `follower` / `user`
        // / `profile` / etc. Flatten every entry to a plain person object
        // so PersonRow gets a consistent shape.
        const norm = list.map((it) => extractPerson(it, kind)).filter(Boolean);
        setPeople((prev) => (p === 1 ? norm : [...prev, ...norm]));
        setHasMore(list.length >= 20);
      } catch (e) {
        setErr(apiError(e));
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [endpoint, username, kind]
  );

  useEffect(() => {
    setPage(1);
    load(1);
  }, [load]);

  const follow = async (id, uname) => {
    setFollowingIds((s) => new Set(s).add(id));
    try {
      if (uname) {
        await api.post(EP.followByUsername, { user_id: id, username: uname });
      } else {
        await api.post(EP.followUser(id), {});
      }
      toast.success("Followed.");
    } catch (e) {
      setFollowingIds((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
      toast.error(apiError(e));
    }
  };

  const unfollow = async (id) => {
    setFollowingIds((s) => {
      const n = new Set(s);
      n.delete(id);
      return n;
    });
    try {
      await api.delete(EP.unfollowUser(id));
    } catch (e) {
      setFollowingIds((s) => new Set(s).add(id));
      toast.error(apiError(e));
    }
  };

  const isFollowing = (u) => {
    // Follow endpoints key on the users-table id — rows carry it as
    // user_id (their own id can be the profile-row id).
    const id = u.user_id ?? u.id;
    if (followingIds.has(id)) return true;
    return !!u.viewer_is_following || !!u.is_following;
  };

  const title = mode === "followers" ? "Followers" : "Following";

  return (
    <div className="max-w-2xl mx-auto w-full px-4 sm:px-6 py-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        data-testid="userlist-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.24em] text-muted-foreground mb-2">
          @{username}
        </p>
        <h1
          className="font-display text-3xl md:text-4xl font-medium tracking-tighter"
          data-testid={`userlist-title-${mode}`}
        >
          {title}
        </h1>
      </div>

      {loading && people.length === 0 ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-16 rounded-2xl bg-foreground/5 animate-pulse"
            />
          ))}
        </div>
      ) : err ? (
        <div
          className="rounded-3xl border border-destructive/40 bg-destructive/10 text-destructive p-6"
          data-testid={`userlist-error-${mode}`}
        >
          {err}
        </div>
      ) : people.length === 0 ? (
        <div
          className="rounded-3xl border border-border p-10 text-center text-muted-foreground"
          data-testid={`userlist-empty-${mode}`}
        >
          {mode === "followers"
            ? "No followers yet."
            : "Not following anyone yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {people.map((raw, i) => (
            <PersonRow
              key={String(raw?.user_id ?? raw?.id ?? i)}
              item={raw}
              currentUser={user}
              isFollowing={isFollowing}
              onFollow={follow}
              onUnfollow={unfollow}
            />
          ))}
          {hasMore && (
            <div className="pt-4">
              <PillButton
                variant="ghost"
                className="w-full"
                onClick={() => {
                  const n = page + 1;
                  setPage(n);
                  load(n);
                }}
                disabled={loading}
                data-testid={`userlist-loadmore-${mode}`}
              >
                <span className="inline-flex items-center gap-2">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Load more
                </span>
              </PillButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * PersonRow — a single follower/following row. If the incoming item
 * only carries a bare `user_id` (backend follow-edge rows sometimes do
 * that), `useAuthor` fetches `GET /users/{id}` once per session to
 * hydrate the username / display_name / avatar.
 */
function PersonRow({ item, currentUser, isFollowing, onFollow, onUnfollow }) {
  const rawId = item?.user_id ?? item?.id;
  // Always run useAuthor — its internal `hasEnoughInline` short-circuits
  // when the row already carries display_name + username + an avatar
  // field, so it's a no-op for fully-inlined rows and a hydration for
  // rows that are missing name, username, or avatar (the typical case
  // for follow-edge responses).
  const hydrated = useAuthor(rawId != null ? rawId : null, item);
  // Merge: hydrated is the base; inline `item` fields win when present
  // (so viewer_is_following / follow_pending / bio embedded on the edge
  // aren't clobbered).
  const clean = (o) => {
    const out = {};
    if (!o || typeof o !== "object") return out;
    for (const [k, v] of Object.entries(o)) {
      if (v !== undefined && v !== null && v !== "") out[k] = v;
    }
    return out;
  };
  const u = { ...clean(hydrated), ...clean(item) };
  const id = u.user_id ?? u.id ?? rawId;
  const isSelf =
    currentUser &&
    (id === currentUser.id || (u.username && u.username === currentUser.username));
  const following = isFollowing(u);
  // We're still waiting for `useAuthor` to resolve identity + avatar.
  // Show a light skeleton row instead of a jumping "?" placeholder.
  const hydrating = !!hydrated?._hydrating && !u.username;

  if (hydrating) {
    return (
      <div
        className="flex items-center gap-3 p-3 rounded-2xl border border-border"
        data-testid={`userlist-item-loading-${rawId}`}
      >
        <div className="w-10 h-10 rounded-full bg-foreground/5 animate-pulse flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="h-3.5 rounded bg-foreground/5 animate-pulse w-1/3" />
          <div className="h-3 rounded bg-foreground/5 animate-pulse w-1/4" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl border border-border"
      data-testid={`userlist-item-${id}`}
    >
      <Link
        to={u.username ? `/${u.username}` : "#"}
        className="flex items-center gap-3 flex-1 min-w-0"
      >
        <UserAvatar user={u} size="md" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {u.display_name || u.username || (rawId ? `User ${rawId}` : "User")}
          </div>
          {u.username ? (
            <div className="text-xs text-muted-foreground truncate">
              @{u.username}
            </div>
          ) : null}
          {u.bio ? (
            <div className="text-xs text-muted-foreground truncate mt-0.5">
              {u.bio}
            </div>
          ) : null}
        </div>
      </Link>
      {!isSelf && id != null && (
        <button
          onClick={() =>
            following ? onUnfollow(id) : onFollow(id, u.username)
          }
          className={`h-9 rounded-full px-4 text-xs font-medium border transition-all ${
            following
              ? "bg-foreground text-background border-foreground"
              : "border-border hover:bg-foreground/5"
          }`}
          data-testid={`userlist-follow-${id}`}
        >
          <span className="inline-flex items-center gap-1.5">
            {following ? (
              <Check className="w-3.5 h-3.5" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}
            {following ? "Following" : "Follow"}
          </span>
        </button>
      )}
    </div>
  );
}
